import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { BuildingIcon } from 'lucide-react';
import { LicenceStatusChip } from '@/components/admin/licence-status-chip';
import {
  DataTable,
  dataTableColumnHelper,
} from '@/components/data-table';
import { PageHeader } from '@/components/page-header';
import { Input } from '@/components/ui/input';
import { adminApi, adminKeys, type AdminCustomerRow } from '@/lib/api/admin';
import { messageForError } from '@/lib/api-client';
import { formatDate } from '@/lib/utils';

const PER_PAGE = 25;
const col = dataTableColumnHelper<AdminCustomerRow>();

function CustomersPage() {
  const { page, q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [draft, setDraft] = useState(q ?? '');

  const params = { page, perPage: PER_PAGE, ...(q ? { q } : {}) };
  const query = useQuery({
    queryKey: adminKeys.customers(params),
    queryFn: () => adminApi.customers(params),
    placeholderData: (previous) => previous,
  });

  const columns = [
    col.accessor('name', {
      header: 'Organisation',
      cell: (info) => (
        <div className="flex flex-col">
          <span className="font-medium text-ink">{info.getValue()}</span>
          <span className="font-mono text-micro text-ink-subtle">{info.row.original.slug}</span>
        </div>
      ),
    }),
    col.accessor('licenceStatus', {
      header: 'Licence',
      cell: (info) => <LicenceStatusChip status={info.getValue()} />,
    }),
    col.accessor('planKey', { header: 'Plan', cell: (info) => info.getValue() ?? '—' }),
    col.display({
      id: 'slots',
      header: 'Slots',
      cell: ({ row }) =>
        row.original.slotsTotal === null
          ? '—'
          : `${row.original.slotsUsed ?? 0} / ${row.original.slotsTotal}`,
    }),
    col.accessor('members', { header: 'Members' }),
    col.accessor('sites', { header: 'Sites' }),
    col.accessor('createdAt', { header: 'Created', cell: (info) => formatDate(info.getValue()) }),
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Platform"
        title="Organisations"
        description="Every customer organisation, with licence state and slot usage at a glance."
      />

      <form
        role="search"
        className="max-w-sm"
        onSubmit={(event) => {
          event.preventDefault();
          void navigate({ search: { page: 1, q: draft.trim() || undefined } });
        }}
      >
        <Input
          type="search"
          aria-label="Search organisations"
          placeholder="Search by name or slug"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
      </form>

      <DataTable
        caption="Customer organisations"
        columns={columns}
        data={query.data?.items ?? []}
        isLoading={query.isPending}
        error={query.isError ? messageForError(query.error) : undefined}
        empty={
          <span className="flex flex-col items-center gap-2 text-data text-ink-muted">
            <BuildingIcon className="size-4" />
            No organisations match.
          </span>
        }
        getRowId={(row) => row.id}
        onRowClick={(row) =>
          void navigate({ to: '/admin/customers/$organisationId', params: { organisationId: row.id } })
        }
        pagination={{
          page,
          perPage: PER_PAGE,
          ...(query.data ? { total: query.data.total } : {}),
          onPageChange: (next) => void navigate({ search: { page: next, q } }),
        }}
      />
    </div>
  );
}

export const Route = createFileRoute('/admin/customers/')({
  validateSearch: z.object({
    page: z.coerce.number().int().min(1).catch(1).default(1),
    q: z.string().optional(),
  }),
  component: CustomersPage,
});
